import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Select, MenuItem } from "@mui/material";

import { hideModal } from "../../../../../../../store/slices/modal";
import { generateUuId } from "../../../../../../../utils/helperFunc";
import { VIN_API_URL } from "../../../../../../../utils/api-ends";
import LoaderImg from "../../../../../../../assets/media/loader.gif";
import useHttp from "../../../../../../../hooks/useHttp";
import {
  VALIDATE_LOADS_URL,
  COUNTRY_STATE_N_CITY_LIST_URL,
} from "../../../../../../../utils/api-ends";

const VehicleLoadManageModal = ({ modalData }) => {
  const [year, setYear] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [color, setColor] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [lotNumber, setLotNumber] = useState("");
  const [state, setState] = useState("");
  const [vin, setVin] = useState("");
  const [additionalInformation, setAdditionalInformation] = useState("");
  const [isVinLoading, setIsVinLoading] = useState(false);

  // Error messages state
  const [yearErr, setYearErr] = useState([]);
  const [brandErr, setBrandErr] = useState([]);
  const [modelErr, setModelErr] = useState([]);
  const [vehicleTypeErr, setVehicleTypeErr] = useState([]);
  const [colorErr, setColorErr] = useState([]);
  const [licensePlateErr, setLicensePlateErr] = useState([]);
  const [lotNumberErr, setLotNumberErr] = useState([]);
  const [stateErr, setStateErr] = useState([]);
  const [vinErr, setVinErr] = useState([]);
  const [additionalInformationErr, setAdditionalInformationErr] = useState([]);

  // Onchange function
  const onYearChange = (e) => setYear(e.target.value);
  const onBrandChange = (e) => setBrand(e.target.value);
  const onModelChange = (e) => setModel(e.target.value);
  const onVehicleTypeChange = (e) => setVehicleType(e.target.value);
  const onColorChange = (e) => setColor(e.target.value);
  const onLicensePlateChange = (e) => setLicensePlate(e.target.value);
  const onLotNumberChange = (e) => setLotNumber(e.target.value);
  const onStateChange = (e) => setState(e.target.value);
  const onVinChange = (e) => setVin(e.target.value);
  const onAdditionalInformationChange = (e) =>
    setAdditionalInformation(e.target.value);

  const dispatch = useDispatch();
  const { isForUpdate, item, loadPostContextDispatch } = modalData;
  const [states, setStates] = useState([]);
  const { isLoading, error, sendRequests } = useHttp();
  const { sendRequests: fetchStates } = useHttp();
  const closeModal = () => dispatch(hideModal());

  // Vin api call
  const onVinGrab = async () => {
    setVinErr(""); // Clear error message

    if (vin?.length === 0) {
      setVinErr("VIN cannot be empty");
      return;
    }

    setIsVinLoading(true); // Start loading

    fetch(`${VIN_API_URL}?vin=${vin}`, {
      headers: {
        "content-type": "application/json",
        authorization: "Basic YzdlMGFkMWUtMGI0YS00YjA4LWI1ZTctMmUzZjFmM2IwYzVm",
        "partner-token": "f6a2af1b8570496694cae0714c7dc043",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setIsVinLoading(false); // Stop loading

        if (data?.data?.model) setModel(data?.data?.model);
        if (data?.data?.make) setBrand(data?.data?.make);
        if (data?.data?.year) setYear(data?.data?.year);
        if (data?.data?.color) setColor(data?.data?.color);
        if (data?.data?.licensePlate) setLicensePlate(data?.data?.licensePlate);
        if (data?.data?.lotNumber) setLotNumber(data?.data?.lotNumber);
        if (data?.data?.state) setState(data?.data?.state);
        if (data?.data?.vehicleType) setVehicleType(data?.data?.vehicleType);
        if (data?.data?.additional_information)
          setAdditionalInformation(data?.data?.additional_information);

        if (data.message.code !== 0) {
          setVinErr(data.message.message);
        }
      })
      .catch((error) => {
        setIsVinLoading(false);
      });
  };

  // Load data to input fields when user click on edit button
  useEffect(() => {
    if (isForUpdate) {
      setYear(item.year);
      setBrand(item.brand);
      setModel(item.model);
      setVehicleType(item.vehicle_type);
      setColor(item.color);
      setLicensePlate(item.license_plate);
      setLotNumber(item.lot_number);
      setState(item.state);
      setAdditionalInformation(item.additional_information);
    }
  }, [isForUpdate, item]);

  // Show or hide error messages on mouse enter or leave
  const showErrorMessage = (e) =>
    e.currentTarget.setAttribute("show-tittle", "true");
  const hideErrorMessage = (e) =>
    e.currentTarget.setAttribute("show-tittle", "false");

  const handleSaveData = () => {
    // Clear Error Messages
    setYearErr([]);
    setBrandErr([]);
    setModelErr([]);
    setVehicleTypeErr([]);
    setColorErr([]);
    setLicensePlateErr([]);
    setLotNumberErr([]);
    setStateErr([]);
    setAdditionalInformationErr([]);

    // Validate
    if (year === "") setYearErr("Year is required");
    if (brand === "") setBrandErr("Brand is required");
    if (model === "") setModelErr("Model is required");
    if (vehicleType === "") setVehicleTypeErr("Vehicle Type is required");
    if (color === "") setColorErr("Color is required");
    if (licensePlate === "") setLicensePlateErr("License Plate is required");
    if (lotNumber === "") setLotNumberErr("Lot Number is required");
    if (state === "") setStateErr("State/Region is required");
    if (additionalInformation === "")
      setAdditionalInformationErr("Additional Information is required");

    if (
      year === "" ||
      brand === "" ||
      model === "" ||
      vehicleType === "" ||
      color === "" ||
      licensePlate === "" ||
      lotNumber === "" ||
      state === "" ||
      additionalInformation === ""
    )
      return;

    // Validate data from server
    const response = (data) => {
      // If there are no errors, save data
      if (isForUpdate === false) {
        loadPostContextDispatch({
          type: "ADD_VEHICLE_LOADER_TO_LIST",
          payload: {
            id: generateUuId(8),
            year,
            brand,
            model,
            vehicle_type: vehicleType,
            color,
            license_plate: licensePlate,
            lot_number: lotNumber,
            state,
            additional_information: additionalInformation,
            isExpand: true,
          },
        });
      } else {
        loadPostContextDispatch({
          type: "UPDATE_VEHICLE_LOADER_TO_LIST",
          payload: {
            id: item.id,
            year,
            brand,
            model,
            vehicle_type: vehicleType,
            color,
            license_plate: licensePlate,
            lot_number: lotNumber,
            state,
            additional_information: additionalInformation,
            isExpand: true,
          },
        });
      }

      closeModal();
    };

    sendRequests(
      {
        url: VALIDATE_LOADS_URL,
        method: "POST",
        data: {
          load_type: "vehicle",
          load_data: {
            year,
            brand,
            model,
            vehicle_type: vehicleType,
            color,
            license_plate: licensePlate,
            lot_number: lotNumber,
            state,
            additional_information: additionalInformation,
          },
        },
      },
      response
    );
  };

  // Append error messages
  useEffect(() => {
    if (error?.errors) {
      const { errors } = error;
      if (errors?.year) setYearErr(errors.year);
      if (errors?.brand) setBrandErr(errors.brand);
      if (errors?.model) setModelErr(errors.model);
      if (errors?.vehicle_type) setVehicleTypeErr(errors.vehicle_type);
      if (errors?.color) setColorErr(errors.color);
      if (errors?.license_plate) setLicensePlateErr(errors.license_plate);
      if (errors?.lot_number) setLotNumberErr(errors.lot_number);
      if (errors?.state) setStateErr(errors.state);
      if (errors?.additional_information)
        setAdditionalInformationErr(errors.additional_information);
    }
  }, [error]);

  // // Fetch list of state
  useEffect(() => {
    const response = (data) => {
      setStates(data);
    };
    fetchStates(
      {
        url: COUNTRY_STATE_N_CITY_LIST_URL,
        method: "GET",
        params: {
          data_type: "states",
          country_name: "USA",
        },
      },
      response
    );
  }, []);

  return (
    <div className="col-xl-7 col-lg-8 col-md-10">
      <div className="load-add-edit-modal">
        <div className="row">
          {error?.message && <p className="auth-error">{error.message}</p>}
          <div className="col-md-12 row align-items-center">
            <div className="col-md-8">
              <div className="information-field border-0">
                <div className="input-box-area" single-labe="true">
                  <label> VIN </label>
                  <input
                    type="text"
                    placeholder="VIN"
                    value={vin}
                    onChange={onVinChange}
                  />
                  {vinErr?.length > 0 && (
                    <div
                      className="error"
                      show-tittle="false"
                      onMouseEnter={showErrorMessage}
                      onMouseLeave={hideErrorMessage}
                    >
                      <i className="fa-solid fa-circle-exclamation"></i>
                      <p>{vinErr}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="multiple-btn ">
                <button onClick={onVinGrab}>
                  Grab Vehicle Data
                  {isVinLoading && (
                    <img src={LoaderImg} alt="" style={{ width: "20px" }} />
                  )}
                </button>
              </div>
            </div>
          </div>

          <hr />

          <div className="col-md-6">
            <div className="information-field border-0">
              <div className="input-box-area" single-labe="true">
                <label> Vehicles Brand </label>
                <input
                  type="text"
                  placeholder="Vehicles Brand"
                  value={brand}
                  onChange={onBrandChange}
                />
                {brandErr?.length > 0 && (
                  <div
                    className="error"
                    show-tittle="false"
                    onMouseEnter={showErrorMessage}
                    onMouseLeave={hideErrorMessage}
                  >
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <p>{brandErr}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="information-field border-0">
              <div className="input-box-area" single-labe="true">
                <label> Vehicles Model </label>
                <input
                  type="text"
                  placeholder="Vehicles Model"
                  value={model}
                  onChange={onModelChange}
                />
                {modelErr?.length > 0 && (
                  <div
                    className="error"
                    show-tittle="false"
                    onMouseEnter={showErrorMessage}
                    onMouseLeave={hideErrorMessage}
                  >
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <p>{modelErr}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="information-field border-0">
              <div className="input-box-area" single-labe="true">
                <label>Vehicle Type </label>
                <input
                  type="text"
                  placeholder="Vehicle Type"
                  value={vehicleType}
                  onChange={onVehicleTypeChange}
                />
                {vehicleTypeErr?.length > 0 && (
                  <div
                    className="error"
                    show-tittle="false"
                    onMouseEnter={showErrorMessage}
                    onMouseLeave={hideErrorMessage}
                  >
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <p>{vehicleTypeErr}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="information-field border-0">
              <div className="input-box-area" single-labe="true">
                <label> Vehicles Year </label>
                <input
                  type="text"
                  placeholder="Vehicles Year"
                  value={year}
                  onChange={onYearChange}
                />
                {yearErr?.length > 0 && (
                  <div
                    className="error"
                    show-tittle="false"
                    onMouseEnter={showErrorMessage}
                    onMouseLeave={hideErrorMessage}
                  >
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <p>{yearErr}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="information-field border-0">
              <div className="input-box-area" single-labe="true">
                <label> Vehicles Color </label>
                <input
                  type="text"
                  placeholder="Vehicles Color"
                  value={color}
                  onChange={onColorChange}
                />
                {colorErr?.length > 0 && (
                  <div
                    className="error"
                    show-tittle="false"
                    onMouseEnter={showErrorMessage}
                    onMouseLeave={hideErrorMessage}
                  >
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <p>{colorErr}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="information-field border-0">
              <div className="input-box-area" single-labe="true">
                <label> Vehicles License Plate </label>
                <input
                  type="text"
                  placeholder="Vehicles License Plate"
                  value={licensePlate}
                  onChange={onLicensePlateChange}
                />
                {licensePlateErr?.length > 0 && (
                  <div
                    className="error"
                    show-tittle="false"
                    onMouseEnter={showErrorMessage}
                    onMouseLeave={hideErrorMessage}
                  >
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <p>{licensePlateErr}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="information-field border-0">
              <div className="input-box-area" single-labe="true">
                <label> Vehicle Title State/Region </label>

                <Select
                  name="state"
                  value={state}
                  onChange={onStateChange}
                  fullWidth
                  className="mui-select-field-without-label"
                  inputProps={{ "aria-label": "Without label" }}
                >
                  {states?.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>

                {stateErr?.length > 0 && (
                  <div
                    className="error"
                    show-tittle="false"
                    onMouseEnter={showErrorMessage}
                    onMouseLeave={hideErrorMessage}
                  >
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <p>{stateErr}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="information-field border-0">
              <div className="input-box-area" single-labe="true">
                <label> Vehicles Lot Number </label>
                <input
                  type="text"
                  placeholder="Vehicles Lot Number"
                  value={lotNumber}
                  onChange={onLotNumberChange}
                />
                {lotNumberErr?.length > 0 && (
                  <div
                    className="error"
                    show-tittle="false"
                    onMouseEnter={showErrorMessage}
                    onMouseLeave={hideErrorMessage}
                  >
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <p>{lotNumberErr}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="information-field border-0">
              <div className="input-box-area" single-labe="true">
                <label> Additional Information </label>
                <p>Use this information to add pertinent information about the load, pickup, delivery, contact info, and anything you want us to know</p>
                <textarea
                  name="additional_information"
                  value={additionalInformation}
                  onChange={onAdditionalInformationChange}
                ></textarea>
                {additionalInformationErr?.length > 0 && (
                  <div
                    className="error"
                    show-tittle="false"
                    onMouseEnter={showErrorMessage}
                    onMouseLeave={hideErrorMessage}
                  >
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <p>{additionalInformationErr}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="multiple-btn modal-button">
          <button onClick={handleSaveData}>
            {" "}
            Save{" "}
            {isLoading && (
              <img src={LoaderImg} alt="" style={{ width: "20px" }} />
            )}
          </button>
          <button onClick={closeModal}> Cancel </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(VehicleLoadManageModal);
